/**
 * Cascalo cart guard: keeps the Add to cart → cart drawer → checkout path deliberate.
 *
 * Horizon opens the cart drawer when an add finishes (on an empty cart that is ~1 s after the tap).
 * On phones the drawer's "Check out" / PayPal buttons then appear exactly where the shopper's thumb
 * is (the sticky Add to cart bar sits at the bottom of the screen), so an impatient second tap went
 * straight to checkout, and a quick double tap added the item twice.
 *
 * Built on Horizon's own CartLinesUpdateEvent (dispatched by every add path: product form, sticky
 * bar, quick add, Cascalo cards/FBT/cross-sell), this module:
 *  1. ignores repeat taps on any Add to cart button while an add is in flight (no duplicate lines);
 *  2. while an add settles, and for a short moment after the drawer shows the result, makes the
 *     drawer's actions (checkout, accelerated checkout, cross-sell, links) ignore taps and shows
 *     them dimmed, so checkout only opens from a deliberate tap.
 */
import { StandardEvents } from '@shopify/events';

const ADD_BUTTONS = 'button[name="add"], .add-to-cart-button, cascalo-quick-add button[type="submit"]';
const SETTLE_AFTER_ADD_MS = 1800;
const MAX_GUARD_MS = 8000; // never leave the drawer locked if a request hangs

const root = document.documentElement;
let inFlight = 0;
let settleTimer = 0;
let safetyTimer = 0;

const drawerDialog = () => document.querySelector('#cart-drawer dialog');

function release() {
  clearTimeout(settleTimer);
  clearTimeout(safetyTimer);
  inFlight = 0;
  root.removeAttribute('data-cx-adding');
  drawerDialog()?.removeAttribute('data-cx-settling');
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add') return;

  inFlight += 1;
  clearTimeout(settleTimer);
  root.setAttribute('data-cx-adding', '');
  drawerDialog()?.setAttribute('data-cx-settling', '');
  clearTimeout(safetyTimer);
  safetyTimer = setTimeout(release, MAX_GUARD_MS);

  const finished = () => {
    inFlight = Math.max(0, inFlight - 1);
    if (inFlight) return;
    root.removeAttribute('data-cx-adding');
    // The drawer may have been re-rendered: re-apply on the current dialog until the delay ends.
    drawerDialog()?.setAttribute('data-cx-settling', '');
    settleTimer = setTimeout(release, SETTLE_AFTER_ADD_MS);
  };

  Promise.resolve(event.promise).then(finished, finished);
});

// Capture phase: runs before Horizon's and Cascalo's own handlers.
document.addEventListener(
  'click',
  (event) => {
    if (!root.hasAttribute('data-cx-adding')) return;
    const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    if (!target?.closest(ADD_BUTTONS)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);

document.addEventListener(
  'submit',
  (event) => {
    if (!root.hasAttribute('data-cx-adding')) return;
    const form = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
    if (!form?.matches('form[action*="/cart/add"]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  },
  true
);
